import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { isLoaded } from 'react-redux-firebase';
import { map, filter, some, values, compact, orderBy, isEmpty } from 'lodash';

// library components
import { Formik, Form } from 'formik';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

//validation library
import * as Yup from 'yup';

// cr components
import ButtonSpinner from 'components/ButtonSpinner/ButtonSpinner';
import CustomSelect from 'components/CustomFormComponents/CustomSelect';

//actions & selectors
import { addContractorToJob } from 'redux/actions/staffingActions';
import { useStaffingSelector } from 'redux/selectors/staffing/staffingSelectors';
import { checkProjectIsActive, selectProject } from 'redux/selectors/staffing/getProject';

import CustomSearchableSelect from 'components/CustomFormComponents/CustomSearchableSelect';

const useStyles = makeStyles(theme => ({
  dialogActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing(4),
  },
  cancelButton: {
    width: 105,
    height: 40,
    marginRight: 'auto',
    background: '#9E9E9E',
    color: '#fff',
    fontSize: 14,
    '&:hover': {
      background: '#9E9E9E',
      opacity: 0.9,
    },
  },
  saveButton: {
    width: 105,
    height: 40,
    background: theme.palette.primary.main,
    color: '#fff',
    fontSize: 14,
    '&:hover': {
      background: theme.palette.primary.main,
      opacity: 0.9,
    },
    '&:disabled': {
      background: '#9E9E9E',
      color: '#fff',
    },
  },
  selectContainer: {
    paddingBottom: theme.spacing(4), // Adds 32px of padding to the bottom
  },
}));

const AddJobToContractorForm = ({ closeDialog }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');

  const { contractorToUpdateId } = useStaffingSelector();

  // Unfiltered Firestore maps — list selectors (selectProjects/selectDeals) apply staffing page filters.
  const projectsById = useSelector(state => state.firestore.data.projects);
  const dealsById = useSelector(state => state.firestore.data.deals);

  const getProject = selectProject();
  const selectedProject = useSelector(state => getProject(state, selectedProjectId));

  const formInitialValues = {
    project: selectedProjectId,
    job: '',
  };

  const formSchema = Yup.object().shape({
    project: Yup.string().required('Required'),
    job: Yup.string().required('Required'),
  });

  const availableJobFilter = job => {
    const assignees = values(job.assignees);
    const contractorAssigned = some(
      assignees,
      assignee => assignee.contractorId === contractorToUpdateId,
    );
    const signedCount = filter(assignees, assignee =>
      ['signed', 'paid'].includes(assignee.status),
    ).length;
    return !contractorAssigned && signedCount < job.seats;
  };

  /**
   * Return Active Projects
   */
  const getProjectSelectOptions = () => {
    if (!isLoaded(projectsById) || !isLoaded(dealsById)) {
      return [];
    }

    const activeProjects = filter(values(projectsById), project => {
      if (!checkProjectIsActive(project)) {
        return false;
      }
      if (isEmpty(project.jobs)) {
        return false;
      }

      const availableJobs = filter(project.jobs, availableJobFilter);
      return availableJobs.length > 0;
    });

    const options = compact(
      map(activeProjects, project => {
        const deal = project.dealId ? dealsById[project.dealId] : undefined;
        const companyName = deal?.companyName ?? project.companyName;

        if (!companyName) {
          return null;
        }

        const name = deal?.dealName
          ? `${project.eventName} - ${deal.dealName} - ${companyName}`
          : `${project.eventName} - ${companyName}`;

        return {
          name,
          value: project.id,
        };
      }),
    );

    return orderBy(options, option => option.name.toLowerCase());
  };

  /**
   * Get Jobs in Project
   */
  const getJobSelectOptions = () => {
    if (selectedProject) {
      const availableJobs = filter(selectedProject.jobs, availableJobFilter);

      return map(availableJobs, job => ({
        name: `${job.jobName} - ${job.jobRole}`,
        value: job.jobId,
      }));
    }

    return [];
  };

  const handleFormSubmit = values => {
    setIsSubmitting(true);

    const successCallback = () => {
      setIsSubmitting(false);
      closeDialog();
    };

    dispatch(addContractorToJob(values.project, values.job, contractorToUpdateId, successCallback));
  };

  return (
    <Formik
      validationSchema={formSchema}
      initialValues={formInitialValues}
      onSubmit={async values => {
        handleFormSubmit(values);
      }}
    >
      {({ submitForm, resetForm, setFieldValue }) => (
        <Form style={{ paddingBottom: 20 }}>
          <div className={classes.selectContainer}>
            <CustomSearchableSelect
              label="Project"
              name="project"
              placeholder=""
              disableClearable
              afterChange={value => {
                setSelectedProjectId(value); //set the Project Id in state to derive the Jobs for the Selected Project
              }}
              valueOverride={{ name: selectedProject?.eventName, value: selectedProject?.id }}
              options={getProjectSelectOptions()}
              noOptionsText={'No projects with unassigned jobs'}
              // customStyles={{
              //   container: classes.selectContainer,
              //   label: classes.inputLabel,
              // }}
            />
          </div>

          {selectedProjectId && (
            <CustomSelect
              label="Job"
              className="mb-4"
              name="job"
              type="select"
              placeholder=""
              required
              selectOptions={getJobSelectOptions()}
            />
          )}

          <div className={classes.dialogActions}>
            <Button
              className={classes.cancelButton}
              disabled={isSubmitting}
              onClick={() => {
                closeDialog();
                resetForm();
              }}
            >
              CANCEL
            </Button>
            <Button className={classes.saveButton} disabled={isSubmitting} onClick={submitForm}>
              ADD
              {isSubmitting && <ButtonSpinner />}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

AddJobToContractorForm.propTypes = {
  closeDialog: PropTypes.func.isRequired,
};

export default AddJobToContractorForm;
